import type { PortalUserListItem } from '@/api/generated';
import { toPortalUserRow, type PortalUserRow } from '@/lib/portalUsers';

/**
 * What staff may do to one row of the Super Admins card.
 *
 * The row itself is read the shared way, in `src/lib/portalUsers.ts`, so staff
 * and the organization's own Users page never disagree about who is Invited
 * and who is Expired. What may be done to it is staff's own decision, and
 * differs from the Users page's on purpose: staff do not revoke an expired
 * link, and they only see Super Admins.
 */
export interface SuperAdminRow extends PortalUserRow {
  canResend: boolean;
  canRevoke: boolean;
}

export function toSuperAdminRow(item: PortalUserListItem): SuperAdminRow {
  const row = toPortalUserRow(item);
  return {
    ...row,
    // Resending an expired link is the commonest reason to resend at all, so
    // both waiting states offer it. There is nothing to resend without an
    // invitation to replace.
    canResend:
      (row.state === 'invited' || row.state === 'expired') && Boolean(row.invitationId),
    // Revoke kills a link nobody has used yet. An accepted one is not a link
    // any more, and removing that person is the organization's own business.
    canRevoke: row.state === 'invited' && Boolean(row.invitationId),
  };
}

/**
 * The card lists Super Admins. An organization's Admins belong to the Users
 * page the organization runs itself, and showing them here would suggest staff
 * manage them, which they do not.
 */
export function toSuperAdminRows(items: PortalUserListItem[]): SuperAdminRow[] {
  return items.filter((item) => item.role === 'SUPER_ADMIN').map(toSuperAdminRow);
}
